(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Carousel = global.NewHomeSource.Carousel || {};

  class DragTracker {
    /**
     * Initializes a new instance of the class.
     *
     * @constructor
     * @property {any} state - The current state of the tracker.
     * @property {number} logInterval - The interval (in ms) for logging events. Default is 170.
     * @property {Event|null} startEvent - The initial event when tracking starts.
     * @property {Event|null} lastEvent - The most recent event tracked.
     */
    constructor() {
      this.state = null;
      this.logInterval = 170;
      this.startEvent = null;
      this.lastEvent = null;
    }

    /**
     * Initializes the drag tracker with the provided state.
     * @param {Object} state - The state object to initialize the drag tracker with.
     */
    initialize(state) {
      this.state = state;
    }

    /**
     * Reads the coordinate value from a mouse or touch event along the specified axis.
     *
     * @param {MouseEvent|TouchEvent} evt - The event object, either a MouseEvent or TouchEvent.
     * @param {'x'|'y'} [evtAxis] - Optional axis override ('x' or 'y'). If not provided, uses the axis from state.
     * @returns {number} The coordinate value (clientX or clientY) of the event along the specified axis.
     */
    readPoint(evt, evtAxis) {
      const axis = this.state.get('axis');
      const property = evtAxis || axis.scroll;
      const coord = property === 'x' ? 'clientX' : 'clientY';
      const point = evt instanceof global.MouseEvent ? evt : evt.touches[0];
      return point[coord];
    }

    /**
     * Handles the pointer down event for the drag tracker.
     * Stores the initial and last event, and reads the starting point.
     *
     * @param {PointerEvent} evt - The pointer down event object.
     * @returns {Object} The point data extracted from the event.
     */
    pointerDown(evt) {
      this.startEvent = evt;
      this.lastEvent = evt;
      return this.readPoint(evt);
    }

    /**
     * Handles pointer move events by calculating the difference between the current and last pointer positions.
     * Updates the last event and, if the log interval has expired, resets the start event.
     *
     * @param {Event} evt - The pointer move event object.
     * @returns {number} The difference in pointer position since the last event.
     */
    pointerMove(evt) {
      const diff = this.readPoint(evt) - this.readPoint(this.lastEvent);
      const expired = DragTracker.readTime(evt) - DragTracker.readTime(this.startEvent) > this.logInterval;
      this.lastEvent = evt;
      if (expired) {
        this.startEvent = evt;
      }

      return diff;
    }

    /**
     * Handles the pointer up event to determine if a flick gesture occurred.
     *
     * Calculates the drag distance and time difference between the start and end of a drag,
     * and determines if the gesture qualifies as a "flick" based on speed and timing.
     *
     * @param {Event} evt - The pointer up event object.
     * @returns {number} The calculated force of the flick if a flick is detected, otherwise 0.
     */
    pointerUp(evt) {
      if (!this.startEvent || !this.lastEvent) {
        return 0;
      }

      const diffDrag = this.readPoint(this.lastEvent) - this.readPoint(this.startEvent);
      const diffTime = DragTracker.readTime(evt) - DragTracker.readTime(this.startEvent);
      const expired = DragTracker.readTime(evt) - DragTracker.readTime(this.lastEvent) > this.logInterval;
      const force = diffDrag / diffTime;
      const isFlick = diffTime && !expired && Math.abs(force) > 0.1;

      return isFlick ? force : 0;
    }

    /**
     * Retrieves the timestamp from the given event.
     *
     * @param {Event} evt - The event object from which to read the timestamp.
     * @returns {number} The time at which the event was created, in milliseconds.
     */
    static readTime(evt) {
      return evt.timeStamp;
    }
  }

  global.NewHomeSource.Carousel.DragTracker = DragTracker;
}(window));
